import { MissingFieldsNotice } from "@/components/submissions/MissingFieldsNotice";
import { TOUCH_TARGET } from "@/components/buttonStyles";
import {
  carrierRefusalLine,
  type CarrierStepError,
  type MissingField,
} from "@/lib/submissions/brokerSubmissions";

// What the carrier step can refuse, in this carrier's place (BRK-S290):
// missing insured details, answers the carrier didn't accept, a setup problem
// that is Signal's to fix, the carrier being unreachable, or an outdated
// price. Shown by the questions loop, and by the quote popup for a refusal
// that landed while the broker was elsewhere (BRK-S347).
export function CarrierStepProblem({
  problem,
  carrier,
  onCompleteProfile,
  missingFieldHref,
  onRetry,
}: {
  problem: CarrierStepError;
  carrier: string;
  onCompleteProfile?: () => void;
  missingFieldHref?: (field: MissingField) => string | null;
  onRetry?: () => void;
}) {
  switch (problem.kind) {
    case "missingFields":
      return (
        <MissingFieldsNotice
          missing={problem.missing}
          onCompleteProfile={onCompleteProfile}
          missingFieldHref={missingFieldHref}
        />
      );
    case "rejected":
      return (
        <div role="alert" className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-brk-danger">
            {carrier} didn’t accept these answers:
          </span>
          <ul className="list-inside list-disc text-brk-text">
            {problem.messages.map((m) => (
              <li key={m}>{m}</li>
            ))}
          </ul>
          <span className="text-brk-dim">
            {problem.saved
              ? "Your answers are saved. Review the carrier’s message and try again."
              : "Nothing from this round was saved. Update the answers and try again."}
          </span>
        </div>
      );
    case "carrier":
      // In the broker's words, never PMS's sentence for Signal's staff
      // (BRK-S496).
      return (
        <p role="status" className="text-sm text-brk-dim">
          {carrierRefusalLine(problem, carrier)}
        </p>
      );
    case "unavailable":
      return (
        <div role="alert" className="flex flex-col gap-1 text-sm">
          <span className="text-brk-danger">
            {carrier} isn’t responding right now. Try again in a few minutes.
          </span>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className={`self-start text-brk-accent hover:underline ${TOUCH_TARGET}`}
            >
              Try again
            </button>
          )}
        </div>
      );
    case "incomplete":
      return (
        <p role="status" className="text-sm text-brk-dim">
          {carrier} has a few more questions — answer them to continue.
        </p>
      );
    case "outdated":
      return (
        <p role="alert" className="text-sm text-brk-danger">
          {carrier}’s price is out of date: the application changed after it was
          priced. Go back to the quotes and get an updated quote.
        </p>
      );
    case "fieldErrors":
      return null; // shown on each question
    // Another request still holds the carrier (PMS-S815): PMS's own words,
    // and retryable.
    case "busy":
    case "other":
      return (
        <div role="alert" className="flex flex-col gap-1 text-sm">
          <span className="text-brk-danger">{problem.message}</span>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className={`self-start text-brk-accent hover:underline ${TOUCH_TARGET}`}
            >
              Try again
            </button>
          )}
        </div>
      );
  }
}
